// Browser translation can turn schema values such as `10px` into invalid `10 px` or `10 pixels`.
const translatedCssUnitPattern = /(?<![\w.-])([+-]?(?:\d+(?:\.\d+)?|\.\d+))\s*(pixels?|percent(?:age)?s?|px|r?em|%|vw|vh|deg|ms|s)(?![a-z])/gi;
function normalizeTranslatedCssElement(element) {
  for (const property of element.style) {
    if (!property.startsWith('--')) continue;
    const value = element.style.getPropertyValue(property);
    const normalized = value.replace(translatedCssUnitPattern, (_, number, unit) => {
      const normalizedUnit = unit.toLowerCase();
      if (normalizedUnit.startsWith('pixel')) return `${number}px`;
      if (normalizedUnit.startsWith('percent')) return `${number}%`;
      return `${number}${normalizedUnit}`;
    });
    if (normalized !== value) {
      element.style.setProperty(property, normalized, element.style.getPropertyPriority(property));
    }
  }
}
function normalizeTranslatedCssUnits(root = document) {
  if (root.matches?.('[style]')) normalizeTranslatedCssElement(root);
  root.querySelectorAll('[style]').forEach(normalizeTranslatedCssElement);
}
normalizeTranslatedCssUnits();
new MutationObserver((mutations) => mutations.forEach((mutation) => {
  if (mutation.type === 'attributes') normalizeTranslatedCssElement(mutation.target);
  else mutation.addedNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) normalizeTranslatedCssUnits(node);
  });
})).observe(document.documentElement, {
  subtree: true, childList: true, attributes: true, attributeFilter: ['style'],
});

const pendingTabCarousels = new WeakMap();

function initSwiper({
  swiperSelector,
  swiperOptions = {},
  autoplayResumeDelay = null,
}) {
  const swiperEl = typeof swiperSelector === 'string'
    ? document.querySelector(swiperSelector)
    : swiperSelector;

  if (!swiperEl) return null;

  // Tab markup is already rendered; defer only the hidden panel's Swiper instance.
  for (let panel = swiperEl.closest('[data-tab-panel]'); panel;
    panel = panel.parentElement?.closest('[data-tab-panel]')) {
    if (!panel.classList.contains('is-active')) {
      pendingTabCarousels.set(swiperEl, {
        swiperSelector: swiperEl, swiperOptions, autoplayResumeDelay,
      });
      return null;
    }
  }
  pendingTabCarousels.delete(swiperEl);

  const previous = swiperEl.swiper;
  if (previous && !previous.destroyed) {
    previous.destroy(true, true);
  }
  const swiper = new Swiper(swiperEl, {
    speed: 500,
    ...swiperOptions,
  });
  if (
    autoplayResumeDelay !== null
    && swiperOptions.autoplay
    && swiper.autoplay
  ) {
    initSwiperAutoplayResume(swiper, autoplayResumeDelay);
  }
  return swiper;
}

function syncSwiperCssGrid(swiper) {
  const { params, el, wrapperEl } = swiper;
  const rows = Number(params.grid?.rows) || 1;
  const perView = Number(params.slidesPerView);
  const enabled = params.cssMode && swiper.isHorizontal() && rows > 1
    && params.grid.fill === 'row' && !params.loop && !params.virtual?.enabled
    && Number.isFinite(perView) && perView > 0;

  el.classList.toggle('swiper-css-grid', Boolean(enabled));
  if (!enabled) return;

  const space = params.spaceBetween;
  const gap = typeof space === 'string' && space.endsWith('%')
    ? swiper.size * parseFloat(space) / 100
    : Number(space) || 0;
  const columns = Math.max(1, Math.ceil(swiper.slides.length / rows), Math.floor(perView));
  wrapperEl.style.setProperty('--swiper-grid-rows', rows);
  wrapperEl.style.setProperty('--swiper-grid-columns', columns);
  wrapperEl.style.setProperty('--swiper-grid-gap', `${gap}px`);
}

function initSwiperAutoplayResume(swiper, delay = 1500) {
  const baseSpeed = swiper.params.speed;
  const autoplayDirection = swiper.params.autoplay?.reverseDirection ? 'prev' : 'next';

  let resumeTimer = null;
  let resumeFrame = null;
  let touching = false;
  let waitingForFirstLeg = false;
  let gestureLoopAnchor = null;

  const cancelFirstLeg = () => {
    if (!waitingForFirstLeg) return;
    waitingForFirstLeg = false;
    swiper.off('transitionEnd', completeFirstLeg);
  };

  const cancelResume = () => {
    if (resumeTimer !== null) {
      clearTimeout(resumeTimer);
      resumeTimer = null;
    }
    if (resumeFrame !== null) {
      cancelAnimationFrame(resumeFrame);
      resumeFrame = null;
    }
  };

  const syncState = (translate) => {
    swiper.updateProgress(translate);
    swiper.updateActiveIndex();
    swiper.updateSlidesClasses();
  };

  const axisStart = (el) => {
    const rect = el.getBoundingClientRect();
    return swiper.isHorizontal() ? rect.left : rect.top;
  };

  const renderedTransitionMs = () => {
    if (!swiper.animating) return 0;

    const style = getComputedStyle(swiper.wrapperEl);
    const parseTime = (value) => {
      const number = Number.parseFloat(value) || 0;
      return value.trim().endsWith('ms') ? number : number * 1000;
    };
    const durations = style.transitionDuration.split(',').map(parseTime);
    const delays = style.transitionDelay.split(',').map(parseTime);

    return Math.min(700, durations.reduce((max, duration, index) => (
      Math.max(max, duration + (delays[index] ?? delays[0] ?? 0))
    ), 0));
  };

  const freezeAtRenderedPosition = () => {
    const translate = swiper.getTranslate();

    if (swiper.autoplay.running) {
      swiper.autoplay.stop();
    }

    swiper.setTransition(0);
    swiper.setTranslate(translate);
    syncState(translate);
    swiper.touchEventsData.velocities.length = 0;

    if (swiper.animating) {
      swiper.wrapperEl.dispatchEvent(new CustomEvent('transitionend', {
        bubbles: true,
        cancelable: true,
        detail: { bySwiperTouchMove: true },
      }));
    }
  };

  const preserveLoopFrame = () => {
    if (!swiper.params.loop) return;

    const anchor = swiper.slides[swiper.activeIndex] || swiper.slides[0];
    if (!anchor) return;

    const beforePosition = axisStart(anchor);
    const beforeTranslate = swiper.getTranslate();

    swiper.loopFix({ direction: autoplayDirection, slideTo: false });

    const physicalCorrection = beforePosition - axisStart(anchor);
    const translateCorrection = swiper.isHorizontal() && swiper.rtlTranslate
      ? -physicalCorrection
      : physicalCorrection;

    if (Math.abs(translateCorrection) > 0.01) {
      swiper.setTransition(0);
      swiper.setTranslate(beforeTranslate + translateCorrection);
    }
  };

  // Native loopFix can snap a fractional free-mode position when recycling slides.
  // Keep the same card under the finger; Swiper still owns the drag and momentum.
  swiper.on('beforeLoopFix', () => {
    if (!touching) return;
    const slide = swiper.slides[swiper.activeIndex];
    gestureLoopAnchor = slide ? { slide, position: axisStart(slide) } : null;
  });
  swiper.on('loopFix', () => {
    if (!gestureLoopAnchor) return;
    const { slide, position } = gestureLoopAnchor;
    gestureLoopAnchor = null;
    const correction = position - axisStart(slide);
    if (Math.abs(correction) < 0.01) return;
    const translate = swiper.getTranslate() + (
      swiper.isHorizontal() && swiper.rtlTranslate ? -correction : correction
    );
    swiper.setTransition(0);
    swiper.setTranslate(translate);
    syncState(translate);
  });

  const firstLegSpeed = () => {
    const group = Math.max(1, Number(swiper.params.slidesPerGroup) || 1);
    const activeIndex = swiper.activeIndex;
    const targetIndex = autoplayDirection === 'next'
      ? activeIndex + group
      : activeIndex - group;
    const activeSlide = swiper.slides[activeIndex];
    const targetSlide = swiper.slides[targetIndex];

    if (!activeSlide || !targetSlide) {
      return baseSpeed;
    }
    const fullDistance = Math.abs(axisStart(targetSlide) - axisStart(activeSlide));
    const remainingDistance = Math.abs(axisStart(targetSlide) - axisStart(swiper.el));
    if (fullDistance < 1 || remainingDistance < 1) return baseSpeed;

    return Math.round(Math.min(
      baseSpeed,
      Math.max(80, baseSpeed * remainingDistance / fullDistance),
    ));
  };

  const startNativeAutoplay = () => {
    if (!swiper.autoplay.running) {
      swiper.autoplay.start();
    } else if (swiper.autoplay.paused) {
      swiper.autoplay.resume();
    }
  };

  function completeFirstLeg() {
    if (!waitingForFirstLeg) return;

    waitingForFirstLeg = false;
    swiper.off('transitionEnd', completeFirstLeg);
    if (touching || swiper.destroyed) return;

    preserveLoopFrame();
    syncState();
    startNativeAutoplay();
  }

  const startAutoplay = () => {
    resumeFrame = null;
    if (touching || swiper.destroyed) return;

    preserveLoopFrame();
    syncState();

    const group = Math.max(1, Number(swiper.params.slidesPerGroup) || 1);
    const targetIndex = autoplayDirection === 'next'
      ? swiper.activeIndex + group
      : swiper.activeIndex - group;

    if (!swiper.slides[targetIndex]) {
      startNativeAutoplay();
      return;
    }

    waitingForFirstLeg = true;
    swiper.on('transitionEnd', completeFirstLeg);
    const didMove = swiper.slideTo(targetIndex, firstLegSpeed(), true, true);

    if (didMove === false) {
      cancelFirstLeg();
      startNativeAutoplay();
    }
  };

  const scheduleResume = (wait) => {
    if (resumeTimer !== null) clearTimeout(resumeTimer);
    resumeTimer = setTimeout(() => {
      resumeTimer = null;
      resumeFrame = requestAnimationFrame(startAutoplay);
    }, Math.max(0, wait));
  };

  const finishGesture = () => {
    if (!touching || swiper.destroyed) return;

    touching = false;
    if (resumeFrame !== null) cancelAnimationFrame(resumeFrame);
    resumeFrame = requestAnimationFrame(() => {
      resumeFrame = null;
      // Wait for idle after the last release, including a tap without dragging.
      scheduleResume(Math.max(delay, renderedTransitionMs()));
    });
  };

  const onPointerCancel = () => {
    if (!touching) return;

    Object.assign(swiper.touchEventsData, {
      isTouched: false,
      isMoved: false,
      startMoving: false,
      allowTouchCallbacks: false,
      pointerId: null,
      touchId: null,
    });
    finishGesture();
  };

  const beginGesture = (event) => {
    if (touching || swiper.destroyed || (event?.button ?? 0) > 0) return;

    cancelResume();
    cancelFirstLeg();
    touching = true;
    freezeAtRenderedPosition();
  };
  swiper.el.addEventListener('pointerdown', beginGesture, {
    capture: true,
    passive: true,
  });
  swiper.on('touchStart', beginGesture);

  swiper.on('touchEnd', finishGesture);
  document.addEventListener('pointerup', finishGesture, true);
  document.addEventListener('pointercancel', onPointerCancel, true);

  swiper.on('destroy', () => {
    cancelResume();
    cancelFirstLeg();
    swiper.el?.removeEventListener('pointerdown', beginGesture, true);
    document.removeEventListener('pointerup', finishGesture, true);
    document.removeEventListener('pointercancel', onPointerCancel, true);
  });
}

function destroySwiper(sw) {
  if (!sw?.swiper || sw.swiper.destroyed) return null;
  sw.swiper.destroy(true, true);
  return null;
}

function hiddenSlides(swiper) {
  const isMobile = window.innerWidth < 800;
  const indexRemove = [];
  swiper.slides.forEach((slide, index) => {
    const hideOnMobile = slide.dataset.hideMobile === 'true';
    const hideOnDesktop = slide.dataset.hideDesktop === 'true';
    if (
      (isMobile && hideOnMobile) ||
      (!isMobile && hideOnDesktop)
    ) {
      indexRemove.push(index);
    }
  });
  if (!indexRemove.length) return;
  swiper.removeSlide(indexRemove);
  swiper.update();
}

/* Handle tabmenu builder 1.1 */
const tabMenuActiveState = new Map();

function getTabMenuElements(root, selector) {
  return [...root.querySelectorAll(selector)].filter(
    (element) => element.closest('[data-tab-menu]') === root
  );
}

function updateTabPanelCarousels(panel) {
  panel.querySelectorAll('[data-carousel-swiper]').forEach((swiperEl) => {
    const pending = pendingTabCarousels.get(swiperEl);
    if (pending) {
      initSwiper(pending);
      return;
    }
    const swiper = swiperEl.swiper;
    if (swiper && !swiper.destroyed) swiper.update();
  });
}

function activateTabButton(root, tabId) {
  const activeClass = 'is-active';
  const buttons = getTabMenuElements(root, '[data-tab-btn]');
  const panels = getTabMenuElements(root, '[data-tab-panel]');
  const activeButton = buttons.find((button) => button.dataset.tabId === tabId);
  const activePanel = panels.find((panel) => panel.dataset.tabId === tabId);

  if (!activeButton || !activePanel) return false;
  if (root.dataset.activeTabId === tabId) return true;

  buttons.forEach((button) => {
    const active = button.dataset.tabId === tabId;

    button.classList.toggle(activeClass, active);
    button
      .querySelector('.button-style')
      ?.classList.toggle(activeClass, active);
  });

  panels.forEach((panel) => {
    panel.classList.toggle(activeClass, panel === activePanel);
  });

  root.dataset.activeTabId = tabId;
  tabMenuActiveState.set(root.id, tabId);
  updateTabPanelCarousels(activePanel);
  return true;
}

function syncTabButtonHeads(root) {
  if (typeof root === 'string') root = document.querySelector(root);
  if (!root || root.dataset.tabReady === 'true') return;

  const head = getTabMenuElements(root, '[data-tab-heads]')[0];
  const panels = getTabMenuElements(root, '[data-tab-panel]');

  if (!head || !panels.length) return;

  const buttonMap = new Map(
    getTabMenuElements(root, '[data-tab-btn]').map((button) => [
      button.dataset.tabId,
      button
    ])
  );

  /* Move all tab buttons to the shared head */
  panels.forEach((panel) => {
    const button = buttonMap.get(panel.dataset.tabId);
    if (button) head.append(button);
  });

  const firstTabId = panels.find((panel) => (
    buttonMap.has(panel.dataset.tabId)
  ))?.dataset.tabId;
  const activeTabId = tabMenuActiveState.get(root.id) || firstTabId;

  if (!activateTabButton(root, activeTabId) && firstTabId) {
    activateTabButton(root, firstTabId);
  }

  root.dataset.tabReady = 'true';
}

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-tab-btn]');

  if (!button || !button.closest('[data-tab-heads]')) return;

  const root = button.closest('[data-tab-menu]');
  if (!root) return;

  event.preventDefault();
  activateTabButton(root, button.dataset.tabId);
});

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-tab-menu]');
    const owner = event.target.closest?.('[data-tab-btn], [data-tab-panel]');

    if (root && owner?.dataset.tabId) {
      activateTabButton(root, owner.dataset.tabId);
    }
  });
}
